import React from 'react';
import { Alert, Button, Card, DatePicker, Input, Row, Col, Space, Statistic, Table, Tag, Typography } from 'antd';

import { platformOperationalHealthService } from '../../services/api';

const { Title, Text } = Typography;

const HEALTH_COLOR = {
  HEALTHY: 'green',
  ATTENTION: 'gold',
  DEGRADED: 'orange',
  CRITICAL: 'red',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const getErrorMessage = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const PlatformOperationalHealthPage = () => {
  const [filters, setFilters] = React.useState({
    tenant_id: '',
    range: null,
  });
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [data, setData] = React.useState(null);

  const loadHealth = async () => {
    setLoading(true);
    setError('');

    const params = {
      tenant_id: (filters.tenant_id || '').trim() || undefined,
      from_date: filters.range?.[0] ? filters.range[0].startOf('day').toISOString() : undefined,
      to_date: filters.range?.[1] ? filters.range[1].endOf('day').toISOString() : undefined,
    };

    try {
      const response = await platformOperationalHealthService.get(params);
      setData(response?.data || null);
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Nao foi possivel carregar Operational Health.'));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadHealth();
  }, []);

  const summary = data?.summary || {};
  const health = data?.health || { status: '-', reasons: [] };

  const sourceColumns = [
    { title: 'Source', dataIndex: 'source', key: 'source' },
    { title: 'Count', dataIndex: 'count', key: 'count' },
  ];

  const eventTypeColumns = [
    { title: 'Event Type', dataIndex: 'event_type', key: 'event_type' },
    { title: 'Count', dataIndex: 'count', key: 'count' },
  ];

  const recentErrorColumns = [
    { title: 'Occurred At', dataIndex: 'occurred_at', key: 'occurred_at', render: (value) => formatDateTime(value) },
    { title: 'Severity', dataIndex: 'severity', key: 'severity', render: (value) => <Tag color={HEALTH_COLOR[value] || 'default'}>{value}</Tag> },
    { title: 'Tenant', dataIndex: 'tenant_id', key: 'tenant_id' },
    { title: 'Source', dataIndex: 'source', key: 'source' },
    { title: 'Event Code', dataIndex: 'event_code', key: 'event_code' },
    { title: 'Title', dataIndex: 'title', key: 'title' },
    { title: 'Correlation ID', dataIndex: 'correlation_id', key: 'correlation_id' },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Operational Health</Title>
      <Text type="secondary">Visao agregada read-only da saude operacional do SaaS Billing.</Text>

      {error ? (
        <Alert type="error" showIcon style={{ marginTop: 16 }} message="Falha ao carregar" description={error} />
      ) : null}

      <Card size="small" title="Filtros" style={{ marginTop: 16 }}>
        <Space wrap>
          <Input
            placeholder="tenant_id"
            value={filters.tenant_id}
            onChange={(event) => setFilters((prev) => ({ ...prev, tenant_id: event.target.value }))}
            style={{ width: 260 }}
          />
          <DatePicker.RangePicker
            showTime
            value={filters.range}
            onChange={(value) => setFilters((prev) => ({ ...prev, range: value }))}
          />
          <Button type="primary" onClick={loadHealth} loading={loading}>Atualizar</Button>
        </Space>
      </Card>

      <Card size="small" title="Status Geral" style={{ marginTop: 16 }}>
        <Space direction="vertical" size={4}>
          <Tag color={HEALTH_COLOR[health.status] || 'default'}>{health.status || '-'}</Tag>
          {(health.reasons || []).map((reason) => (
            <Text key={reason} type="secondary">{reason}</Text>
          ))}
        </Space>
      </Card>

      <Card size="small" title="Metricas" style={{ marginTop: 16 }} loading={loading}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}><Statistic title="Total Events" value={summary.total_events || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="Info" value={summary.info_events || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="Warning" value={summary.warning_events || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="Error" value={summary.error_events || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="Critical" value={summary.critical_events || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="Unique Tenants" value={summary.unique_tenants || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="Events 24h" value={summary.events_last_24h || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="CR Pending" value={summary.pending_change_requests || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="CR Approved" value={summary.approved_change_requests || 0} /></Col>
          <Col xs={24} sm={12} md={6}><Statistic title="CR Rejected" value={summary.rejected_change_requests || 0} /></Col>
        </Row>
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card size="small" title="By Source">
            <Table
              rowKey={(row) => row.source}
              dataSource={data?.by_source || []}
              columns={sourceColumns}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card size="small" title="By Event Type">
            <Table
              rowKey={(row) => row.event_type}
              dataSource={data?.by_event_type || []}
              columns={eventTypeColumns}
              pagination={false}
              size="small"
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Recent Errors" style={{ marginTop: 16 }}>
        <Table
          rowKey={(row) => row.id}
          dataSource={data?.recent_errors || []}
          columns={recentErrorColumns}
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          scroll={{ x: 1100 }}
        />
      </Card>
    </div>
  );
};

export default PlatformOperationalHealthPage;
